import React from 'react';
import { INotification, NotificationType } from '@/types/notification';
import { NotificationService } from '@/api/notification';
import useTenantState from '@/states/tenant.state';
import useNotificationState from '@/states/notification.state';
import { MessageSquare, CheckCircle, UserPlus, Bell, CheckCheck, ExternalLink } from 'lucide-react';
import { TimeAgo } from '@/components/shared/timeAgo/TimeAgo';
import { useNavigate } from 'react-router-dom';
import MainTable, { MainTableColumn } from '@/components/company/table/MainTable';
import { useDefaultDataConfig } from '@/utils/hooks/useDefaultDataConfig';

// Get icon based on notification type
const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'App\\Notifications\\DiscussionMessage':
      return <MessageSquare size={18} className="text-blue-500" />;
    case 'App\\Notifications\\ACKConfirmation':
      return <CheckCircle size={18} className="text-green-500" />;
    case 'App\\Notifications\\PICAssignment':
      return <UserPlus size={18} className="text-purple-500" />;
    default:
      return <Bell size={18} className="text-gray-500" />;
  }
};

export const NotificationPage: React.FC = () => {
  const { currentTenant } = useTenantState();
  const { markAsRead, markAllAsRead } = useNotificationState();
  const navigate = useNavigate();
  const { dataConfig: pageConfig, setDataConfig: setPageConfig } = useDefaultDataConfig();

  const {
    data: notificationData,
    refetch,
    isLoading,
  } = NotificationService.useGetNotifications({
    tenantId: currentTenant?.id || 0,
    page: pageConfig.page + 1, // API uses 1-based pagination
    pageSize: pageConfig.limit,
    enabled: !!currentTenant?.id,
  });

  const handleNotificationClick = async (notification: INotification) => {
    // Mark as read if not already
    if (!notification.is_read && currentTenant?.id) {
      await markAsRead(currentTenant.id, notification.id);
      refetch();
    }

    // Navigate to action URL
    if (notification.data.action_url) {
      navigate(`/${currentTenant?.slug}${notification.data.action_url}`);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (currentTenant?.id) {
      await markAllAsRead(currentTenant.id);
      refetch();
    }
  };

  const columns: MainTableColumn[] = [
    {
      name: 'type',
      label: 'Tipe',
      render: (_: unknown, row: INotification) => (
        <div className="flex items-center gap-2">
          {getNotificationIcon(row.type)}
          <span className="text-sm text-gray-700">{row.type_label}</span>
        </div>
      ),
    },
    {
      name: 'data.title',
      label: 'Pesan',
      render: (_: unknown, row: INotification) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium text-base text-gray-900">{row.data.title}</span>
          <span className="text-sm text-gray-600 whitespace-normal">{row.data.message}</span>
          {row.data.sender_name && <span className="text-xs text-gray-400">From: {row.data.sender_name}</span>}
        </div>
      ),
    },
    {
      name: 'created_at',
      label: 'Waktu',
      render: (_: unknown, row: INotification) => <TimeAgo timestamp={row.created_at} />,
    },
    {
      name: 'is_read',
      label: 'Status',
      render: (_: unknown, row: INotification) =>
        row.is_read ? (
          <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">Dibaca</span>
        ) : (
          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Belum Dibaca</span>
        ),
    },
    {
      name: 'action',
      label: 'Aksi',
      className: 'text-center',
      render: (_: unknown, row: INotification) => (
        <div className="flex items-center justify-center">
          <button
            onClick={() => handleNotificationClick(row)}
            className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
            title="Lihat detail"
          >
            <ExternalLink size={18} />
          </button>
        </div>
      ),
    },
  ];

  const paginatedData = {
    content: (notificationData?.notifications || []) as unknown as { [key: string]: unknown }[],
    totalElements: notificationData?.total || 0,
    totalPages: Math.ceil((notificationData?.total || 0) / pageConfig.limit),
  };

  return (
    <div className="p-5 bg-white rounded w-full flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Notifikasi</h3>
          {notificationData?.unread_count !== undefined && notificationData.unread_count > 0 && (
            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
              {notificationData.unread_count} belum dibaca
            </span>
          )}
        </div>
        {notificationData?.unread_count !== undefined && notificationData.unread_count > 0 && (
          <button
            className="flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
            onClick={handleMarkAllAsRead}
          >
            <CheckCheck size={16} />
            Tandai Semua Dibaca
          </button>
        )}
      </div>

      {/* Notification Table */}
      <MainTable
        showHeader={false}
        isLoading={isLoading}
        data={paginatedData}
        columns={columns}
        pageConfig={pageConfig}
        setPageConfig={setPageConfig}
        showActions={false}
        sectionClassName="px-0"
      />
    </div>
  );
};
